import {
    faDownload,
    faExpand,
    faFolderGear,
    faInfoCircle,
    faMinimize,
} from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useAnalyticsApiClient, useAppSelector } from "../../../hooks";
import { ExpandableArea, useAreaExpansion } from "../../../ui/ExpandableArea";
import { FloatingMenu } from "../../../ui/Menu";
import { Modal } from "../../../ui/Modals";
import type { AnalyticsFilters } from "../state";
import { getQuarterDateRange } from "../utils/quarterUtils";
import { useFilterText } from "../utils/useFilterText";
import { useImageDownload } from "../utils/useImageDownload";
import { AggregatedQuarterGlobalAdmin } from "./admin";
import { PercentagePlot } from "./percentagePlot";

interface AggregatedQuarterDataProps {
    filters: AnalyticsFilters;
}

export const AggregatedQuarterData = ({
    filters,
}: AggregatedQuarterDataProps) => {
    const analyticsApi = useAnalyticsApiClient();
    const userIsStaff = useAppSelector((state) => state.auth.isStaff);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const svgRef = useRef<SVGSVGElement>(null);
    const filterText = useFilterText();
    const { downloadImage } = useImageDownload(
        svgRef,
        "emissions_per_quarter",
        filterText,
    );

    // Query 1: Emissions counts per quarter
    const emissionsQuery = useQuery({
        queryKey: [
            "analyticsEmissionsPerQuarterProvidersWithNonDetects",
            filters.quarterFrom,
            filters.quarterTo,
            filters.detectionLimit,
            filters.providers,
            filters.locationPreset,
            filters.siteType,
        ],
        queryFn: async () => {
            const startRange = getQuarterDateRange(filters.quarterFrom);
            const endRange = getQuarterDateRange(filters.quarterTo);

            return await analyticsApi.analyticsEmissionsCountsPerQuarterList({
                dataProviderHasNonDetects: true,
                timestampAfter: startRange.start,
                timestampBefore: endRange.end,
                detectionThresholdGte: (filters.detectionLimit ?? 100) * 1000,
                providerWithSource:
                    filters.providers === "all" || !filters.providers
                        ? undefined
                        : (JSON.stringify(filters.providers) as any),
                locationPreset: filters.locationPreset?.length
                    ? filters.locationPreset
                    : undefined,
                siteType: filters.siteType?.length
                    ? filters.siteType
                    : undefined,
            });
        },
        refetchOnWindowFocus: false,
        staleTime: 300,
    });

    // Query 2: Non-detects counts per quarter
    const nonDetectsQuery = useQuery({
        queryKey: [
            "analyticsNonDetectsPerQuarter",
            filters.quarterFrom,
            filters.quarterTo,
            filters.detectionLimit,
            filters.providers,
            filters.locationPreset,
            filters.siteType,
        ],
        queryFn: async () => {
            const startRange = getQuarterDateRange(filters.quarterFrom);
            const endRange = getQuarterDateRange(filters.quarterTo);

            return await analyticsApi.analyticsNondetectsCountsPerQuarterList({
                timestampAfter: startRange.start,
                timestampBefore: endRange.end,
                detectionThreshold: (filters.detectionLimit ?? 100) * 1000,
                providerWithSource:
                    filters.providers === "all" || !filters.providers
                        ? undefined
                        : (JSON.stringify(filters.providers) as any),
                locationPreset: filters.locationPreset?.length
                    ? filters.locationPreset
                    : undefined,
                siteType: filters.siteType?.length
                    ? filters.siteType
                    : undefined,
            });
        },
        refetchOnWindowFocus: false,
        staleTime: 300,
    });

    // Query 3: Emissions counted as non-detects! (below threshold)
    const emissionsBelowThresholdQuery = useQuery({
        queryKey: [
            "analyticsEmissionsBelowThresholdPerQuarterProvidersWithNonDetects",
            filters.quarterFrom,
            filters.quarterTo,
            filters.detectionLimit,
            filters.providers,
            filters.locationPreset,
            filters.siteType,
        ],
        queryFn: async () => {
            const startRange = getQuarterDateRange(filters.quarterFrom);
            const endRange = getQuarterDateRange(filters.quarterTo);

            return await analyticsApi.analyticsEmissionsCountsPerQuarterList({
                dataProviderHasNonDetects: true,
                timestampAfter: startRange.start,
                timestampBefore: endRange.end,
                detectionThresholdLt: (filters.detectionLimit ?? 100) * 1000,
                detectionThresholdGte: 1,
                providerWithSource:
                    filters.providers === "all" || !filters.providers
                        ? undefined
                        : (JSON.stringify(filters.providers) as any),
                locationPreset: filters.locationPreset?.length
                    ? filters.locationPreset
                    : undefined,
                siteType: filters.siteType?.length
                    ? filters.siteType
                    : undefined,
            });
        },
        refetchOnWindowFocus: false,
        staleTime: 300,
    });

    const emissions = emissionsQuery.data || [];
    const nonDetects = nonDetectsQuery.data || [];
    const emissionsBelowThreshold = emissionsBelowThresholdQuery.data || [];
    const isLoading =
        emissionsQuery.isLoading ||
        nonDetectsQuery.isLoading ||
        emissionsBelowThresholdQuery.isLoading;

    const { expandedCardId, expandCard, contractCard } = useAreaExpansion();
    const extraActions = useMemo(() => {
        const actions = [
            {
                icon: faInfoCircle,
                title: "Plot information",
                fn: () => setShowInfoModal(true),
            },
            {
                icon: faDownload,
                title: "Download plot as image (JPEG)",
                fn: downloadImage as () => void,
            },
        ];
        if (userIsStaff) {
            actions.push({
                icon: faFolderGear,
                title: "Download data as CSV (admin only)",
                fn: () => setIsDownloadModalOpen(true),
            });
        }

        const isExpanded = expandedCardId === "obs_per_quarter";
        return [
            ...actions,
            {
                icon: isExpanded ? faMinimize : faExpand,
                title: "Expand plot",
                fn: () =>
                    isExpanded ? contractCard() : expandCard("obs_per_quarter"),
            },
        ];
    }, [downloadImage, userIsStaff, expandedCardId, contractCard, expandCard]);

    return (
        <>
            <ExpandableArea
                cardId="obs_per_quarter"
                expandable={true}
                className="h-full relative"
                content={
                    <div className="h-full p-1 group">
                        <FloatingMenu
                            actions={extraActions}
                            className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                        />
                        <PercentagePlot
                            svgRef={svgRef}
                            emissionsData={emissions}
                            emissionsBelowThreshold={emissionsBelowThreshold}
                            nonDetectsData={nonDetects}
                            loading={isLoading}
                            filters={filters}
                        />
                    </div>
                }
            />

            <Modal
                visible={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                size="sm"
            >
                <div className="my-4 text-sm">
                    <h2 className="text-lg font-semibold mb-4">
                        Plot information
                    </h2>
                    <p className="mb-2">
                        This figure shows the percentage of observations per
                        quarter where the emissions at a site were measured to
                        be over the selected threshold. Each observation and
                        measurement is for an entire site. Data from providers
                        that provide information at the equipment level are
                        aggregated to site level.
                    </p>
                    <p className="mb-2">
                        Since all observations are required, including those
                        with very low or zero measured emissions, this figure
                        only includes data from providers where we can infer
                        non-detects.
                    </p>
                    <p>
                        The percentage of observations over a threshold from
                        previous quarters can be useful for estimating the
                        number of emission events in a future survey. For
                        example, if this figure shows 10% of observations over
                        100 kg/h, then a future survey of one observation over
                        100 sites may find 10 sites over the threshold. The
                        reliability of such an estimate will depend on many
                        factors including how much data was collected.
                    </p>
                </div>
            </Modal>

            {userIsStaff && (
                <Modal
                    visible={isDownloadModalOpen}
                    onClose={() => setIsDownloadModalOpen(false)}
                    size="sm"
                >
                    <div className="mt-4">
                        <h2 className="text-lg font-semibold mb-4">
                            Download Data
                        </h2>
                        <AggregatedQuarterGlobalAdmin
                            emissionsData={emissions}
                            nonDetectsData={nonDetects}
                            belowThresholdData={emissionsBelowThreshold}
                            isLoading={isLoading}
                        />
                    </div>
                </Modal>
            )}
        </>
    );
};
